import { CalendarClock, LogOut, PawPrint, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { getStoredRefresh, useCustomerAuthStore } from './customerAuthStore';
import { CUSTOMER_LOGOUT_MUTATION } from './portal.gql';
import { portalApolloClient } from './portalApolloClient';

const NAV = [
  { to: '/portal/pets', label_key: 'portal.nav_pets', icon: PawPrint },
  { to: '/portal/appointments', label_key: 'portal.nav_appointments', icon: CalendarClock },
  { to: '/portal/profile', label_key: 'portal.nav_profile', icon: User },
] as const;

export function PortalShell(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const customer = useCustomerAuthStore((s) => s.customer);
  const clear = useCustomerAuthStore((s) => s.clear);

  const onLogout = (): void => {
    // Best-effort server-side revoke of the rotating refresh-token family (never block logout).
    const refresh_token = getStoredRefresh();
    if (refresh_token) {
      void portalApolloClient
        .mutate({ mutation: CUSTOMER_LOGOUT_MUTATION, variables: { input: { refresh_token } } })
        .catch(() => undefined);
    }
    clear();
    void portalApolloClient.clearStore().catch(() => undefined);
    navigate('/portal/login', { replace: true });
  };

  return (
    <div className="bg-muted/20 mx-auto flex min-h-screen max-w-md flex-col">
      <header className="bg-background sticky top-0 z-10 flex items-center justify-between border-b px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">{t('portal.app_name')}</p>
          <p className="text-muted-foreground truncate text-xs">{customer?.full_name ?? ''}</p>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="text-muted-foreground flex items-center gap-1 text-xs"
          aria-label={t('portal.logout')}
        >
          <LogOut className="h-4 w-4" aria-hidden />
          {t('portal.logout')}
        </button>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pb-20">
        <Outlet />
      </main>

      <nav className="bg-background fixed inset-x-0 bottom-0 z-10 mx-auto flex max-w-md border-t">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-0.5 py-2 text-xs ${
                isActive ? 'text-primary font-medium' : 'text-muted-foreground'
              }`
            }
          >
            <item.icon className="h-5 w-5" aria-hidden />
            {t(item.label_key)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
